import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { auth, database } from '../firebase';
import { ref, update, onValue } from 'firebase/database';
import { CURRENCIES, DEFAULT_CURRENCY, getCurrency, getCurrencyFromTimezone } from '../utils/currencyConfig';

const CurrencyContext = createContext();
const CURRENCY_KEY = 'forecast_currency';

export const CurrencyProvider = ({ children }) => {
  const [currencyCode, setCurrencyCode] = useState(DEFAULT_CURRENCY);
  const [isCurrencyLoaded, setIsCurrencyLoaded] = useState(false);

  // Load from localStorage on mount, then sync with Firebase
  useEffect(() => {
    // Step 1: Load from localStorage (instant)
    const saved = localStorage.getItem(CURRENCY_KEY);
    if (saved && CURRENCIES[saved]) {
      setCurrencyCode(saved);
    }
    setIsCurrencyLoaded(true);

    // Step 2: Listen to Firebase for cross-device sync
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) return;

      const userCurrencyRef = ref(database, `users/${user.uid}/currency`);
      const unsub = onValue(userCurrencyRef, (snapshot) => {
        const firebaseCurrency = snapshot.val();
        if (firebaseCurrency && CURRENCIES[firebaseCurrency]) {
          setCurrencyCode(firebaseCurrency);
          localStorage.setItem(CURRENCY_KEY, firebaseCurrency);
        } else if (!saved) {
          // No local or Firebase preference — auto-detect from timezone
          const detected = getCurrencyFromTimezone(
            Intl.DateTimeFormat().resolvedOptions().timeZone
          );
          setCurrencyCode(detected);
          localStorage.setItem(CURRENCY_KEY, detected);
        }
      });

      return () => unsub();
    });

    return () => unsubscribe();
  }, []);

  // Set currency with persistence to localStorage and Firebase
  const setCurrency = useCallback((code) => {
    if (!CURRENCIES[code]) {
      console.error(`Invalid currency code: ${code}`);
      return;
    }

    setCurrencyCode(code);
    localStorage.setItem(CURRENCY_KEY, code);

    // Sync to Firebase
    const user = auth.currentUser;
    if (user) {
      const userRef = ref(database, `users/${user.uid}`);
      update(userRef, {
        currency: code,
        currencySymbol: getCurrency(code).symbol,
        currencySetAt: new Date().toISOString(),
      }).catch(error => {
        console.error('Error saving currency to Firebase:', error);
      });
    }
  }, []);

  const currency = useMemo(() => getCurrency(currencyCode), [currencyCode]);
  const symbol = useMemo(() => currency.symbol, [currency]);
  const locale = useMemo(() => currency.locale, [currency]);

  const value = useMemo(() => ({
    currencyCode,
    currency,
    symbol,
    locale,
    setCurrency,
    isCurrencyLoaded,
  }), [currencyCode, currency, symbol, locale, setCurrency, isCurrencyLoaded]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export { CurrencyContext };
